import { GetterTree } from 'vuex';
import { StudyPlanState } from './state';
import RootState from '@/store/root_state';
import { formatDate, isGreaterThan } from '@/helpers/date';
import {
  Activity,
  ActivityType,
  DateActivities,
  Live,
  StudyPlanModel,
  WeekActivities,
} from '@/models/kingslanding/studyPlanModel';
import { StudyPlanTemplateActivities } from '@/models/kingslanding/studyPlanTemplateActivities';
import { AppointmentModel, EnrollmentModel } from '@descomplica/dex-models';
import { groupActivitiesByDate } from '@/helpers/undergrad-study-plan';

const getters: GetterTree<StudyPlanState, RootState> = {
  currentStudyPlanId: (state: StudyPlanState): string => {
    return state.currentStudyPlanDisplayed;
  },
  getStudyPlan: (state: StudyPlanState): StudyPlanModel | undefined => {
    return state.studyPlans[state.currentStudyPlanDisplayed];
  },
  studyPlanClassroomsByEnrollmentDate: (state, studyPlanGetters): number[] => {
    const {
      getClassroomsEnrollments,
      currentDefaultClassroomsIdsByPeriod,
      currentPeriodStartDate,
    } = studyPlanGetters;
    const classroomsPeriodIds: number[] = currentDefaultClassroomsIdsByPeriod;
    const classroomsEnrollments: EnrollmentModel[] = getClassroomsEnrollments(classroomsPeriodIds);
    const periodStartDate: string = currentPeriodStartDate;

    return classroomsPeriodIds.filter((classroomId: number) =>
      classroomsEnrollments.some(
        (enrollment: EnrollmentModel) =>
          enrollment.classroomByClassroomId === classroomId &&
          isGreaterThan(enrollment.createdAt, periodStartDate),
      ),
    );
  },
  studyPlanByCourseModule:
    (state: StudyPlanState) =>
    (courseId: number, moduleId: number): StudyPlanModel | undefined => {
      return Object.values(state.studyPlans).find(
        (studyPlan: StudyPlanModel) =>
          studyPlan.courseId === courseId && studyPlan.moduleId === moduleId,
      );
    },
  isActivityLoaded:
    (state: StudyPlanState) =>
    (activityId: number): boolean => {
      return state.loadedActivities.some((id: number) => activityId === id);
    },
  isLoadingStudyPlanTemplate: (state: StudyPlanState) => {
    return state.isLoadingStudyPlan;
  },
  isLastWeekLoaded: (state, studyPlanGetters): boolean => {
    const studyPlan: StudyPlanModel | undefined = studyPlanGetters.getStudyPlan;
    if (!studyPlan) {
      return false;
    }

    const lastWeek = studyPlan.activities.reduce(
      (week: number, activity: StudyPlanTemplateActivities) =>
        week > activity.week ? week : activity.week,
      0,
    );
    return state.lastNextWeekLoaded === lastWeek;
  },
  isFirstWeekLoaded: (state: StudyPlanState): boolean => state.lastPreviousWeekLoaded === 1,
  activitiesIdsByWeek:
    (state: StudyPlanState) =>
    (week: number): number[] => {
      const { studyPlans, currentStudyPlanDisplayed } = state;
      return studyPlans[currentStudyPlanDisplayed].activities
        .filter((activity: StudyPlanTemplateActivities) => activity.week === week)
        .map((activity: StudyPlanTemplateActivities) => activity.pedagogicalActivityId);
    },
  activitiesByWeek:
    (state, studyPlanGetters) =>
    (week: number): WeekActivities | undefined => {
      const studyPlan: StudyPlanModel | undefined = studyPlanGetters.getStudyPlan;

      if (!studyPlan) {
        return undefined;
      }

      const activitiesByDate = groupActivitiesByDate(studyPlan.activities, week);

      const dateActivities: DateActivities[] = [];
      const { appointments } = studyPlan;

      activitiesByDate.forEach((activities: Activity[], date: string) => {
        const foundAppointment = appointments.find((appointment: AppointmentModel) => {
          const appointmentStart = formatDate(appointment.start, 'DD/MM');

          return appointmentStart === date;
        });

        if (foundAppointment) {
          const live: Live = {
            id: foundAppointment.id,
            type: ActivityType.live,
            start: foundAppointment.start,
            end: foundAppointment.end,
            event: foundAppointment.event,
            classroom: foundAppointment.classroom,
          };
          activities.push(live);
        }

        dateActivities.push({ date, activities });
      });

      return { week, dateActivities };
    },
  currentWeek: (state: StudyPlanState) => state.currentWeek,
  lastPreviousWeekLoaded: (state: StudyPlanState) => state.lastPreviousWeekLoaded,
  lastNextWeekLoaded: (state: StudyPlanState) => state.lastNextWeekLoaded,
};

export default getters;
